import { createSlice, createSelector, PayloadAction } from '@reduxjs/toolkit';

// Define the UI-only state type
interface TeamsUiState {
  // Core UI state
  currentTeamId: string | null;
  currentChannelId: string | null; 
  currentDirectMessageId: string | null;
  
  // UI preferences and selections
  selectedMembers: string[]; // For member selection in UI
  
  // Panel and modal states
  showMembersList: boolean;
  showChannelDetails: boolean;
  
  // Message composition state
  replyToMessageId: string | null;
  isComposing: boolean;
}

// Initial state - UI only
const initialState: TeamsUiState = {
  currentTeamId: null,
  currentChannelId: null,
  currentDirectMessageId: null,
  selectedMembers: [],
  showMembersList: false,
  showChannelDetails: false,
  replyToMessageId: null,
  isComposing: false,
};

// Create the UI slice
const teamsUiSlice = createSlice({
  name: 'teamsUi',
  initialState,
  reducers: {
    // Team/Channel selection (UI navigation)
    setCurrentTeam: (state, action: PayloadAction<string | null>) => {
      state.currentTeamId = action.payload;
      // Clear channel when switching teams
      state.currentChannelId = null;
      state.currentDirectMessageId = null;
    },
    
    setCurrentChannel: (state, action: PayloadAction<string | null>) => {
      state.currentChannelId = action.payload;
      // Clear DM when selecting channel
      state.currentDirectMessageId = null;
    },
    
    setCurrentDirectMessage: (state, action: PayloadAction<string | null>) => {
      state.currentDirectMessageId = action.payload;
      // Clear channel when selecting DM
      state.currentChannelId = null;
    },
    
    // Member selection for UI operations
    setSelectedMembers: (state, action: PayloadAction<string[]>) => {
      state.selectedMembers = action.payload;
    },
    
    addSelectedMember: (state, action: PayloadAction<string>) => {
      if (!state.selectedMembers.includes(action.payload)) {
        state.selectedMembers.push(action.payload);
      }
    },
    
    removeSelectedMember: (state, action: PayloadAction<string>) => {
      state.selectedMembers = state.selectedMembers.filter(id => id !== action.payload);
    },
    
    clearSelectedMembers: (state) => {
      state.selectedMembers = [];
    },
    
    // Panel visibility
    toggleMembersList: (state) => {
      state.showMembersList = !state.showMembersList;
    },
    
    setShowMembersList: (state, action: PayloadAction<boolean>) => {
      state.showMembersList = action.payload;
    },
    
    toggleChannelDetails: (state) => {
      state.showChannelDetails = !state.showChannelDetails;
    },
    
    setShowChannelDetails: (state, action: PayloadAction<boolean>) => {
      state.showChannelDetails = action.payload;
    },
    
    // Message composition
    setReplyToMessage: (state, action: PayloadAction<string | null>) => {
      state.replyToMessageId = action.payload;
    },
    
    startComposing: (state) => {
      state.isComposing = true;
    },
    
    stopComposing: (state) => {
      state.isComposing = false;
      state.replyToMessageId = null;
    },
    
    // Combined selections (legacy compatibility)
    setSelections: (state, action: PayloadAction<{
      teamId?: string | null;
      channelId?: string | null;
      directMessageId?: string | null;
    }>) => {
      const { teamId, channelId, directMessageId } = action.payload;
      if (teamId !== undefined) state.currentTeamId = teamId;
      if (channelId !== undefined) state.currentChannelId = channelId;
      if (directMessageId !== undefined) state.currentDirectMessageId = directMessageId;
    },
    
  },
});

// Export actions
export const {
  setCurrentTeam,
  setCurrentChannel,
  setCurrentDirectMessage,
  setSelectedMembers,
  addSelectedMember,
  removeSelectedMember,
  clearSelectedMembers,
  toggleMembersList,
  setShowMembersList,
  toggleChannelDetails,
  setShowChannelDetails,
  setReplyToMessage,
  startComposing,
  stopComposing,
  setSelections,
} = teamsUiSlice.actions;

// Selectors — typed with slice state to avoid circular dep on RootState
type WithTeamsUi = { teamsUi: TeamsUiState };
export const selectCurrentTeamId = (state: WithTeamsUi) => state.teamsUi.currentTeamId;
export const selectCurrentChannelId = (state: WithTeamsUi) => state.teamsUi.currentChannelId;
export const selectCurrentDirectMessageId = (state: WithTeamsUi) => state.teamsUi.currentDirectMessageId;
export const selectSelectedMembers = (state: WithTeamsUi) => state.teamsUi.selectedMembers;
export const selectShowMembersList = (state: WithTeamsUi) => state.teamsUi.showMembersList;
export const selectShowChannelDetails = (state: WithTeamsUi) => state.teamsUi.showChannelDetails;
export const selectReplyToMessageId = (state: WithTeamsUi) => state.teamsUi.replyToMessageId;
export const selectIsComposing = (state: WithTeamsUi) => state.teamsUi.isComposing;

// Compound selectors (memoized to prevent new object references on every call)
export const selectCurrentConversation = createSelector(
  [(s: WithTeamsUi) => s.teamsUi.currentTeamId,
   (s: WithTeamsUi) => s.teamsUi.currentChannelId,
   (s: WithTeamsUi) => s.teamsUi.currentDirectMessageId],
  (teamId, channelId, directMessageId) => ({ teamId, channelId, directMessageId })
);

export const selectIsChannelSelected = (state: WithTeamsUi) =>
  !!(state.teamsUi.currentChannelId && !state.teamsUi.currentDirectMessageId);

export const selectIsDirectMessageSelected = (state: WithTeamsUi) =>
  !!(state.teamsUi.currentDirectMessageId && !state.teamsUi.currentChannelId);

export default teamsUiSlice.reducer; 